// filepath: apps/web/src/api/client.ts
//
// ISS-377 (uat-ba note_13124) — the single SPA API client.
//
// Every authenticated /api/* fetch from the SPA flows through this
// module. Wraps `window.fetch`, injects the `Authorization: Bearer
// <access_token>` header from the OIDC `useAuth()` context, drives
// the 401 → silent-refresh → retry chain, and on refresh failure
// redirects the user back through the OIDC login flow (preserving
// the originating page via a `returnTo` query param + sessionStorage
// mirror so the LoginRoute can navigate to it on callback).
//
// Why one client (note_13124 + the static-guard contract):
//   1. Bearer auth — the Go server's `internal/auth.AuthMiddleware`
//      requires `Authorization: Bearer <token>` (`auth.go:51-62`).
//      Cookie-only auth (`credentials: "include"`) silently 401s
//      every call. The auto-bearer accessor (see
//      `setOIDCUserAccessor` below) makes the bearer the default —
//      callers that omit `auth` get the OIDC bearer attached at
//      REQUEST time from the registered accessor, not from a
//      stale module-load snapshot.
//   2. 401 silent refresh — the host trigger is no longer hard-coded
//      to `*.platform.example`. The trigger is derived from the
//      runtime config (`apiRuntime.ts`) so it works on
//      `uat-ascoa-go.askou.org` and the RZT UAT hosts.
//   3. Failed-refresh re-login — the BA's "discard unsaved form
//      state, do NOT resubmit a mutation" contract. The client
//      preserves the original `init` (method, body, headers,
//      X-Idempotency-Key) across the replay so the server's
//      idempotency cache returns the same response on retry.
//   4. Same-origin guard — the OIDC strategy asserts the request
//      URL's host matches `window.location.host` (or the
//      `allowCrossOrigin: true` opt-in) before attaching the bearer.
//      This is the token-leak defense (INV-5 / FR-AUTH-7).
//
// Auth strategy discriminator (typed):
//   - `auth: { kind: "oidc" }` (default) — uses the OIDC user-state
//     accessor registered by `<AuthProvider>`. Reads the token AT
//     REQUEST time (the accessor returns the current `access_token`;
//     AuthProvider re-registers after every refresh). Same-origin,
//     `credentials: "include"`.
//   - `auth: { kind: "operator-token" }` — uses
//     `useOperatorToken().token`, `credentials: "omit"`, may opt
//     into cross-origin via `allowCrossOrigin: true`. This is the
//     path `/admin/*` and `/operator/*` use.
//
// The two strategies are mechanically disjoint — there is no
// "if (anyToken) attach" heuristic. A typed discriminator narrows
// the implementation to exactly one strategy, and the typed
// `auth.kind` field is the only token-source selector.
//
// Idempotency-key contract (per note_13124):
//   - The caller passes `{ idempotencyKey: true }` (auto-generate a
//     v4 UUID) or `{ idempotencyKey: "explicit-key" }` (reuse a
//     caller-supplied key). Reads (GET) MUST NOT pass this flag —
//     the server's idempotency middleware only kicks in for
//     state-changing methods.
//   - On replay the client REUSES the original `X-Idempotency-Key`
//     so the server's idempotency cache returns the same response.
//   - When `crypto.randomUUID` is unavailable the client falls back
//     to `crypto.getRandomValues` (RFC-4122 v4 byte layout) — a
//     non-trivial random key per call. If neither is available the
//     client throws `IdempotencyKeyUnavailable` instead of shipping
//     the all-zeros literal that would merge distinct mutations into
//     a single response (silent data-loss vector).

import type { AuthContextProps } from "react-oidc-context";
import type { OperatorTokenSnapshot } from "@/operator/useOperatorToken";
import { signinSilentRefresh } from "./singleFlight";
import { getSilentRefreshTriggerHost } from "@/runtime/apiRuntime";

// `getApiBase` is re-exported via the `./api/index.ts` barrel; the
// runtime config is the single source of truth for the SPA's
// API base path.

// ─── OIDC user accessor (auto-bearer plumbing) ──────────────────────────────

/**
 * Module-scoped function returning the current OIDC
 * `AuthContextProps`, or `null` when no auth state is available.
 * The `AuthProvider` registers this on every user-state change
 * (mount, signin, silent refresh, signout), and `apiFetch`'s
 * default OIDC strategy reads the auth through this accessor at
 * REQUEST time (NOT at module-load time — both the access_token
 * and the `signinSilent` reference are mutable; AuthProvider
 * re-registers after every refresh so the next request sees the
 * FRESH auth state).
 *
 * Why this shape returns the full `AuthContextProps` (not just
 * `access_token`): the default OIDC strategy drives the 401 →
 * silent-refresh → retry chain. The accessor therefore must surface
 * the `signinSilent` callback (and `signinRedirect` for the
 * refresh-fail redirect path). Returning the full auth context
 * keeps the round-2 contract that the bearer IS auto-attached
 * AND the silent-refresh machinery IS auto-driven for every
 * OIDC call site that omits `auth`.
 *
 * The accessor returns:
 *   - `undefined` before AuthProvider has registered
 *     (SSR / pre-bootstrap / static rendering).
 *   - `null` when the user is unauthenticated.
 *   - An `AuthContextProps`-shaped object with at least `user`,
 *     `signinSilent`, and `signinRedirect`.
 */
export type OIDCUserAccessor = () => AuthContextProps | null | undefined;

let currentOIDCUserAccessor: OIDCUserAccessor | null = null;

/**
 * Set the OIDC user accessor. Called by `AuthProvider` on every
 * user-state change (mount, signin, silent refresh, signout).
 * Pass `null` to unregister (e.g. on unmount).
 *
 * TESTS: a test escape hatch (`__resetOidcUserAccessorForTests`)
 * is exported so unit tests can restore the default without
 * relying on the AuthProvider lifecycle. Production code MUST NOT
 * call the test escape hatch.
 */
export function setOIDCUserAccessor(accessor: OIDCUserAccessor | null): void {
  currentOIDCUserAccessor = accessor;
}

/**
 * Read the current auth via the registered accessor. Returns
 * `undefined` when no accessor is registered, `null` when the user
 * is unauthenticated, and an auth-shaped object on success.
 */
function readOIDCAuth(): AuthContextProps | null {
  if (currentOIDCUserAccessor === null) return null;
  const result = currentOIDCUserAccessor();
  if (result === null || result === undefined) return null;
  return result as unknown as AuthContextProps;
}

/**
 * Test-only escape hatch. Resets the registered accessor to the
 * default (`null`). Mirrors the no-leak pattern from
 * `useOperatorToken.__resetOperatorTokenForTests` and
 * `__resetRuntimeConfigForTests`. Production code MUST NOT call
 * this — the accessor's lifecycle is owned by `AuthProvider`.
 */
export function __resetOidcUserAccessorForTests(): void {
  currentOIDCUserAccessor = null;
}

// ─── Public types ─────────────────────────────────────────────────────────────

/**
 * Auth strategy discriminator. The two variants are mechanically
 * disjoint: a typed narrowing in `attachAuthHeaders` ensures the
 * OIDC strategy NEVER reads the operator token store, and the
 * operator strategy NEVER reads the OIDC `useAuth()` user.
 */
export type ApiAuthStrategy =
  | { kind: "oidc"; auth: AuthContextProps }
  | { kind: "operator-token"; token: string | null };

/**
 * Idempotency-key contract. The default is `false` — no idempotency
 * key. Pass `true` to auto-generate a v4 UUID; pass a string to
 * reuse a caller-supplied key (the explicit-key path is gated by
 * the dev-only warning in `attachIdempotencyKey`).
 */
export type ApiIdempotencyKey = boolean | string;

/**
 * Options for `apiFetch`. The minimum is the URL; everything else
 * is opt-in with a sensible default (OIDC strategy with auto-bearer
 * via the registered accessor, no idempotency key, no explicit
 * cross-origin).
 */
export interface ApiFetchOptions {
  /** HTTP method. Default: GET. The client forwards the
   *  method into the underlying `RequestInit.method`. */
  method?: string;
  /** Standard `fetch` `RequestInit`. Body, signal, and
   *  extra headers are passed through unchanged. The client adds
   *  `Authorization` and (optionally) `X-Idempotency-Key` to the
   *  outgoing headers — the caller's `headers` are merged in
   *  LAST so the client-supplied headers always win. */
  init?: RequestInit;
  /** Auth strategy. Default: `{ kind: "oidc" }` — the bearer is
   *  read from the registered accessor (set by `<AuthProvider>`)
   *  at request time. If the caller passes this field explicitly
   *  (e.g. to inject a custom `AuthContextProps` or an
   *  operator-token), the explicit value wins over the default. */
  auth?: ApiAuthStrategy;
  /** Auto-attach `X-Idempotency-Key`. `true` generates a v4 UUID;
   *  a string reuses an explicit key (dev-only warning on duplicate
   *  keys in the same session). Default: `false`. */
  idempotencyKey?: ApiIdempotencyKey;
  /** Opt into a cross-origin URL for the `operator-token` strategy.
   *  The OIDC strategy ignores this flag and asserts same-origin
   *  unconditionally. Default: `false`. */
  allowCrossOrigin?: boolean;
  /** When true, the OIDC silent-refresh path is NOT triggered on a
   *  401. The response is returned unchanged so the caller can
   *  branch on it (e.g. the bootstrap `/api/tenant-config` call
   *  uses this — the user is unauthenticated, a 401 means the
   *  tenant does not exist on this realm, not "your token
   *  expired"). Default: `false`. */
  skipSilentRefresh?: boolean;
}

/**
 * ISS-450: typed wire-level error thrown by `apiFetch` for
 * pre-flight failures that prevent the HTTP request from
 * completing. The discriminated `kind` field carries the
 * specific failure mode; the `cause` field carries the
 * original error (when applicable). Wire-level errors are
 * distinct from HTTP-error responses (which `apiFetch`
 * returns as the raw `Response`; the caller's `callApi` /
 * TanStack Query layer then parses the body envelope and
 * throws the typed `ApiError` from
 * `@/definitions/types`).
 *
 * The pre-ISS-450 client threw a plain `Error(msg)` for all
 * 3 wire-level failure modes (SSR non-browser, cross-origin
 * rejection, IdempotencyKeyUnavailable). The plain `Error`
 * had no `kind` discriminator, so callers could not branch
 * on the failure mode (e.g. "refresh-fail → re-login" vs
 * "cross-origin → switch auth strategy"). The fix below
 * unifies the 3 wire-level failures under a single typed
 * class with a `kind` discriminator.
 *
 * Kinds:
 *   - `"ssr"` — `window` is not defined (SSR / non-browser env).
 *     The caller is in Node-side rendering or a static-
 *     build environment; the apiFetch contract is browser-
 *     only (per the file header).
 *   - `"cross-origin"` — the OIDC strategy is being used on
 *     a cross-origin URL (the OIDC access_token is bound to
 *     the current origin; sending it elsewhere is a token
 *     leak per INV-5 / FR-AUTH-7), or the operator-token
 *     strategy is being used on a cross-origin URL
 *     without the `allowCrossOrigin: true` opt-in.
 *   - `"idempotency-key-unavailable"` — the runtime cannot
 *     produce a v4 UUID and the caller asked for
 *     `idempotencyKey: true` (the canonical "I want a fresh
 *     key" flag). The previous behaviour returned the
 *     literal all-zeros UUID — a silent data-loss vector.
 *
 * ISS-450 message policy (per the BA spec): the `message`
 * field of every `ApiFetchWireError` is a developer-facing
 * string (English, technical, includes the target host
 * and the current origin host for the cross-origin
 * rejection case). End-user messages are rendered by the
 * caller's UI layer (which has the locale + design-system
 * i18n context); the wire error's `message` is NEVER
 * surfaced verbatim to the end user.
 */
export type ApiFetchWireErrorKind =
  "ssr" | "cross-origin" | "idempotency-key-unavailable";

export class ApiFetchWireError extends Error {
  readonly kind: ApiFetchWireErrorKind;
  override readonly cause?: unknown;

  constructor(kind: ApiFetchWireErrorKind, message: string, cause?: unknown) {
    super(message);
    this.name = "ApiFetchWireError";
    this.kind = kind;
    if (cause !== undefined) this.cause = cause;
  }
}

/**
 * Typed error thrown by `apiFetch` when neither `crypto.randomUUID`
 * nor `crypto.getRandomValues` is available — the caller asked for
 * `idempotencyKey: true` but the runtime cannot produce a v4 UUID.
 * The previous behaviour (returning the literal
 * `"00000000-0000-4000-8000-000000000000"`) was a silent data-loss
 * vector: every mutation shared one key; the server's idempotency
 * middleware returned the FIRST response for all later distinct
 * writes. Failing closed at the API surface is the only safe choice.
 *
 * ISS-450: this class now extends `ApiFetchWireError` (was
 * `Error` directly). The `kind` is `"idempotency-key-unavailable"`.
 * Callers that `catch (e instanceof ApiFetchWireError)` and
 * discriminate by `kind` handle this case without a second
 * `instanceof` check.
 */
export class IdempotencyKeyUnavailable extends ApiFetchWireError {
  constructor(cause?: unknown) {
    super(
      "idempotency-key-unavailable",
      "apiFetch: cannot generate an idempotency key — neither " +
        "crypto.randomUUID nor crypto.getRandomValues is available " +
        "in this runtime. The caller MUST supply an explicit key " +
        "(idempotencyKey: '...') instead of true.",
      cause,
    );
    this.name = "IdempotencyKeyUnavailable";
  }
}

/**
 * Session-storage slot for the originating-page URL on a
 * 401 → refresh-fail → re-login sequence. Mirrors the
 * OIDC `returnTo` query-param so the LoginRoute navigates
 * to the originating page on the post-callback redirect.
 */
const RETURN_TO_STORAGE_KEY = "ascoa.auth.returnTo";

/**
 * Read the persisted returnTo. The LoginRoute reads this on
 * callback and falls back to the `?returnTo=` query param if
 * the storage slot is empty (e.g. the user opened the
 * callback URL in a different tab).
 */
export function readReturnTo(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage.getItem(RETURN_TO_STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * Clear the persisted returnTo. Called by the LoginRoute after
 * navigation, so a subsequent unrelated 401 does not navigate
 * the user to a stale returnTo target.
 */
export function clearReturnTo(): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(RETURN_TO_STORAGE_KEY);
  } catch {
    // best-effort
  }
}

// ─── Internal helpers ─────────────────────────────────────────────────────────

/**
 * Resolve the request URL's host. Mirrors `silentRefresh.ts`'s
 * `new URL(url, window.location.origin).host` shape so the
 * same-origin check is consistent between the two paths.
 */
function hostOf(url: string): string | null {
  if (typeof window === "undefined") return null;
  try {
    return new URL(url, window.location.origin).host;
  } catch {
    return null;
  }
}

/**
 * Compute the originating-page URL (pathname + search, no hash).
 * Captured at the moment of the failed-refresh so the LoginRoute
 * can navigate to it on callback.
 */
function currentReturnTo(): string {
  if (typeof window === "undefined") return "/";
  return `${window.location.pathname}${window.location.search}`;
}

/**
 * Persist the returnTo to sessionStorage AND build the
 * `extraQueryParams` arg for `auth.signinRedirect`. The sessionStorage
 * slot is the durable record (survives a tab switch or copy-paste
 * of the OIDC authorize URL); the query param is the OIDC-canonical
 * surface.
 *
 * The `signinRedirect` call is wrapped in a try/catch because a
 * unit-test stub or a half-initialised OIDC client may not have
 * the method present. The contract is "the user is redirected
 * to the login flow"; the mechanical details are best-effort.
 */
function redirectToLogin(args: {
  auth: AuthContextProps;
  returnTo: string;
}): void {
  if (typeof window !== "undefined") {
    try {
      window.sessionStorage.setItem(RETURN_TO_STORAGE_KEY, args.returnTo);
    } catch {
      // best-effort
    }
  }
  try {
    if (typeof args.auth.signinRedirect === "function") {
      void args.auth.signinRedirect({
        extraQueryParams: { returnTo: args.returnTo },
      });
    }
  } catch {
    // best-effort — the caller's onError callback still gets
    // the original 401, the SPA's session-storage slot is the
    // durable record.
  }
}

/**
 * Dev-only warning when the explicit-key path is reused within
 * the same session. The server's idempotency cache would dedupe
 * these into a single response, which is the desired behaviour —
 * but a developer who passes the same explicit key for two
 * distinct mutations almost certainly has a bug. We surface the
 * warning in dev (process.env.NODE_ENV !== "production") only.
 */
const seenExplicitKeys = new Set<string>();
function warnIfDuplicateExplicitKey(key: string): void {
  if (
    typeof process !== "undefined" &&
    process.env &&
    process.env.NODE_ENV === "production"
  ) {
    return;
  }
  if (seenExplicitKeys.has(key)) {
    // eslint-disable-next-line no-console
    console.warn(
      `[apiFetch] X-Idempotency-Key "${key}" was reused within the same ` +
        "session. The server's idempotency cache will dedupe the two " +
        "mutations into a single response. If this is unintentional, " +
        "generate a fresh key per call.",
    );
  }
  seenExplicitKeys.add(key);
}

/**
 * Resolve the OIDC auth at request time. Returns the current
 * `AuthContextProps` (which carries `signinSilent` for the
 * 401-refresh path AND `user.access_token` for the bearer
 * attach) when the accessor is registered. Returns `null`
 * otherwise — the apiFetch call then omits the bearer header
 * and the server returns 401, which the apiFetch 401 handler
 * will surface to the caller (no silent-refresh machinery
 * without an `auth` context).
 *
 * When the caller passed an explicit `ApiAuthStrategy` (either
 * the OIDC variant with a custom `AuthContextProps` or the
 * operator-token variant), `attachAuthHeaders` uses the
 * explicit value — the accessor is consulted only for the
 * default OIDC strategy (auth omitted entirely).
 */
function resolveAuthStrategyForRequest(
  options: ApiFetchOptions,
): ApiAuthStrategy {
  if (options.auth !== undefined) return options.auth;
  // Default OIDC strategy. The auth context is read at request
  // time through the accessor; until AuthProvider mounts, the
  // accessor is null and the read returns null (no bearer
  // attached; the call 401s server-side and the bootstrap step
  // re-runs).
  const auth = readOIDCAuth();
  return {
    kind: "oidc",
    auth: auth ?? ({ user: null } as unknown as AuthContextProps),
  };
}

/**
 * Attach the `Authorization: Bearer <token>` header for the chosen
 * auth strategy. The two strategies are mutually exclusive — the
 * `kind` discriminator narrows the type before the token is read.
 *
 * SECURITY:
 *   - OIDC strategy: asserts same-origin (unless the caller
 *     explicitly opts into cross-origin AND the strategy is
 *     operator-token — the OIDC strategy is same-origin
 *     unconditionally).
 *   - Operator-token strategy: never reads `useAuth().user`; the
 *     OIDC token never leaks to the operator path.
 *
 * The function returns a PLAIN OBJECT (not a `Headers` instance)
 * to preserve the original fetch shape — the SPA's existing test
 * suite inspects `call[1]?.headers["X-If-Match"]` and friends
 * via property access (a `Headers` instance would return
 * `undefined` for arbitrary string keys; the property-access
 * shape is the de-facto contract the v1 fetch calls follow).
 */
function attachAuthHeaders(args: {
  authStrategy: ApiAuthStrategy;
  requestUrl: string;
  allowCrossOrigin: boolean;
  existingHeaders: Record<string, string> | undefined;
}): Record<string, string> {
  const headers: Record<string, string> = { ...(args.existingHeaders ?? {}) };
  const host = hostOf(args.requestUrl);

  if (args.authStrategy.kind === "oidc") {
    // OIDC strategy: same-origin only, no cross-origin opt-in.
    // The OIDC `access_token` is bound to the realm and tenant
    // for the current origin; sending it elsewhere is a token
    // leak (INV-5 / FR-AUTH-7).
    if (host !== null && typeof window !== "undefined") {
      if (host !== window.location.host) {
        throw new ApiFetchWireError(
          "cross-origin",
          `apiFetch: refusing to attach OIDC bearer to cross-origin ` +
            `URL (target host "${host}", current origin host ` +
            `"${window.location.host}"). For /operator/* use ` +
            `auth: { kind: "operator-token" } with ` +
            `allowCrossOrigin: true.`,
        );
      }
    }
    const token = args.authStrategy.auth.user?.access_token;
    if (typeof token === "string" && token.length > 0) {
      headers["Authorization"] = `Bearer ${token}`;
    }
  } else {
    // Operator-token strategy: cross-origin is opt-in (the
    // operator console may run on a separate apex host), and
    // `credentials: "omit"` is enforced at the call site
    // (see `buildInit` below). The OIDC token is NEVER
    // consulted in this branch.
    if (!args.allowCrossOrigin) {
      if (host !== null && typeof window !== "undefined") {
        if (host !== window.location.host) {
          throw new ApiFetchWireError(
            "cross-origin",
            `apiFetch: operator-token request to cross-origin URL ` +
              `requires allowCrossOrigin: true (target host "${host}", ` +
              `current origin host "${window.location.host}").`,
          );
        }
      }
    }
    const token = args.authStrategy.token;
    if (typeof token === "string" && token.length > 0) {
      headers["Authorization"] = `Bearer ${token}`;
    }
  }
  return headers;
}

/**
 * Normalise the caller's `headers` option to a plain
 * `Record<string, string>`. The `HeadersInit` type is wide
 * (Headers instance, array-of-tuples, or plain object); the
 * SPA's existing call sites all use the plain-object form, so
 * the normaliser handles Headers instances and tuple arrays
 * for completeness.
 */
function normaliseHeaders(
  init: HeadersInit | undefined,
): Record<string, string> {
  if (init === undefined) return {};
  if (init instanceof Headers) {
    const out: Record<string, string> = {};
    init.forEach((value, key) => {
      out[key] = value;
    });
    return out;
  }
  if (Array.isArray(init)) {
    const out: Record<string, string> = {};
    for (const [k, v] of init) {
      out[k] = v;
    }
    return out;
  }
  return { ...init };
}

/**
 * Build a RFC-4122 v4 UUID from 16 random bytes. Used as the
 * fallback when `crypto.randomUUID` is unavailable (insecure
 * contexts: `http:` on a LAN dev host, older Node, some test
 * environments). The layout is RFC-4122 §4.4:
 *   - byte[6] has the high nibble set to `0x4` (version 4).
 *   - byte[8] has the high two bits set to `0b10` (variant 1).
 */
function uuidv4FromBytes(bytes: Uint8Array): string {
  if (bytes.length !== 16) {
    throw new Error(
      "uuidv4FromBytes: requires a 16-byte buffer (got " +
        bytes.length +
        " bytes)",
    );
  }
  // Stamp the version (4) and variant (RFC-4122) bits in place.
  // The `setUint8Array` rule forbids stamping the buffer twice;
  // make a copy first to keep the caller's buffer untouched.
  const out = new Uint8Array(bytes);
  out[6] = ((out[6] ?? 0) & 0x0f) | 0x40;
  out[8] = ((out[8] ?? 0) & 0x3f) | 0x80;
  // Hex-encode. Use lowercase + standard 8-4-4-4-12 grouping.
  const hex: string[] = [];
  for (let i = 0; i < 16; i += 1) {
    const b = out[i] ?? 0;
    hex.push(b.toString(16).padStart(2, "0"));
  }
  return (
    hex.slice(0, 4).join("") +
    "-" +
    hex.slice(4, 6).join("") +
    "-" +
    hex.slice(6, 8).join("") +
    "-" +
    hex.slice(8, 10).join("") +
    "-" +
    hex.slice(10, 16).join("")
  );
}

/**
 * Generate a fresh RFC-4122 v4 UUID. Tries `crypto.randomUUID`
 * first (the canonical v4 generator). Falls back to
 * `crypto.getRandomValues` with the explicit version/variant
 * stamping. Throws `IdempotencyKeyUnavailable` when NEITHER is
 * available — the previous literal `"00000000-…"` fallback was
 * a silent data-loss vector (every mutation shared one key; the
 * server's idempotency middleware merged distinct writes into a
 * single response).
 */
function generateUid(): string {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.getRandomValues === "function"
  ) {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    return uuidv4FromBytes(bytes);
  }
  throw new IdempotencyKeyUnavailable();
}

/**
 * Attach `X-Idempotency-Key` per the caller's `idempotencyKey` flag.
 * - `false` / `undefined`: no header.
 * - `true`: generate a fresh v4 UUID (via `generateUid`).
 * - string: reuse the caller-supplied key (dev-only warning on
 *   duplicates within the same session).
 */
function attachIdempotencyKey(
  flag: ApiIdempotencyKey | undefined,
  method: string,
): string | null {
  if (flag === undefined || flag === false) return null;
  // Reads MUST NOT auto-attach — the server's idempotency middleware
  // only kicks in for state-changing methods. This is a defensive
  // assertion: a caller that explicitly opts in on a GET is
  // probably mis-using the flag.
  const upper = method.toUpperCase();
  if (upper === "GET" || upper === "HEAD" || upper === "OPTIONS") {
    return null;
  }
  if (flag === true) {
    return generateUid();
  }
  warnIfDuplicateExplicitKey(flag);
  return flag;
}

/**
 * Strip the client-injected headers (`Authorization`,
 * `X-Idempotency-Key`) from the normalised headers so the replay
 * step can re-attach them with the fresh token. The replay needs
 * to re-resolve the bearer (post-refresh) AND re-attach the SAME
 * `X-Idempotency-Key` (preserved across the refresh).
 */
function stripClientInjectedHeaders(
  headers: Record<string, string>,
): Record<string, string> {
  const out: Record<string, string> = { ...headers };
  delete out["Authorization"];
  delete out["X-Idempotency-Key"];
  return out;
}

/**
 * Build the final `RequestInit` for the fetch call. The client's
 * injected headers / credentials / signal are applied LAST so the
 * caller's `init` cannot accidentally clobber them — this is the
 * "only Authorization / credentials / X-Idempotency-Key are added"
 * contract.
 */
function buildInit(args: {
  callerInit: RequestInit | undefined;
  authStrategy: ApiAuthStrategy;
  requestUrl: string;
  allowCrossOrigin: boolean;
  idempotencyKey: ApiIdempotencyKey | undefined;
}): RequestInit {
  const method = (args.callerInit?.method ?? "GET").toUpperCase();
  const headers = attachAuthHeaders({
    authStrategy: args.authStrategy,
    requestUrl: args.requestUrl,
    allowCrossOrigin: args.allowCrossOrigin,
    existingHeaders: normaliseHeaders(args.callerInit?.headers),
  });
  const idemKey = attachIdempotencyKey(args.idempotencyKey, method);
  if (idemKey !== null) {
    headers["X-Idempotency-Key"] = idemKey;
  }
  const init: RequestInit = {
    ...args.callerInit,
    method,
    headers,
  };
  if (args.authStrategy.kind === "oidc") {
    // OIDC strategy: cookies enabled (the OIDC client uses
    // sessionStorage for non-JWT state but the auth flow does
    // not require cookies — `credentials: "include"` is here
    // for parity with the previous behaviour).
    init.credentials = "include";
  } else {
    // Operator-token strategy: cookies MUST be omitted. The
    // operator token is the only auth surface; cross-mixing
    // OIDC cookies would be a token-leak vector.
    init.credentials = "omit";
  }
  return init;
}

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * The single SPA API client. Every authenticated /api/* fetch from
 * the SPA must flow through this function. The static-guard test
 * (`apps/web/src/test/noRawApiFetchForbidlist.test.ts`) fails the
 * build on any raw `fetch('/api/…')` call site outside the
 * allowlist — the per-file allowlist covers `apps/web/src/api/**`
 * (this client + the test fixtures), `apps/web/src/test/**`, and
 * `apps/web/src/runtime/__fixtures__/**`.
 *
 * @param url    The request URL. May be a same-origin path
 *               (`/api/people/123`) or an absolute URL. The OIDC
 *               strategy requires same-origin (or it throws); the
 *               operator-token strategy requires
 *               `allowCrossOrigin: true` for cross-origin URLs.
 * @param options See `ApiFetchOptions`. All fields are optional;
 *                defaults are documented on the interface.
 * @returns A standard `Response` promise. The caller parses the
 *          body, classifies the error envelope, and surfaces the
 *          result via TanStack Query.
 */
export async function apiFetch(
  url: string,
  options: ApiFetchOptions = {},
): Promise<Response> {
  if (typeof window === "undefined") {
    throw new ApiFetchWireError(
      "ssr",
      "apiFetch: window is not defined (SSR / non-browser env)",
    );
  }
  // Resolve the auth strategy AT REQUEST TIME. The default OIDC
  // strategy consults the registered accessor
  // (`setOIDCUserAccessor`) so a refreshed token is read on the
  // next call — not from a stale module-load snapshot. Callers
  // that pass `auth` explicitly bypass the accessor.
  const authStrategy: ApiAuthStrategy = resolveAuthStrategyForRequest(options);
  // Top-level `method` is forwarded into the caller's `init` so
  // both `apiFetch(url, { method: "POST" })` and
  // `apiFetch(url, { init: { method: "POST" } })` work. The latter
  // form is the canonical one — the former is a convenience for
  // mutation-heavy call sites.
  const callerInit: RequestInit = {
    ...(options.init ?? {}),
    ...(options.method !== undefined ? { method: options.method } : {}),
  };
  const init = buildInit({
    callerInit,
    authStrategy,
    requestUrl: url,
    allowCrossOrigin: options.allowCrossOrigin === true,
    idempotencyKey: options.idempotencyKey,
  });

  // First attempt — pass the original request through.
  const firstResponse = await window.fetch(url, init);

  // 401 path — same-host only, unless the caller opted out of
  // the silent-refresh path. The OIDC `access_token` is bound
  // to the current origin; a 401 on a cross-origin request must
  // be returned to the caller for handling.
  if (firstResponse.status !== 401) {
    return firstResponse;
  }
  if (options.skipSilentRefresh === true) {
    return firstResponse;
  }
  if (authStrategy.kind !== "oidc") {
    // Operator-token strategy: a 401 means the operator token
    // is invalid. Surface to the caller (e.g.
    // `useOperatorSummary` clears the token + re-prompts).
    return firstResponse;
  }
  const host = hostOf(url);
  const triggerHost = getSilentRefreshTriggerHost();
  const isTriggerHost =
    host !== null &&
    triggerHost !== null &&
    (triggerHost === host ||
      triggerHost.endsWith("." + host) ||
      host.endsWith("." + triggerHost));
  if (!isTriggerHost) {
    // Off-host 401 — do NOT trigger silent refresh (defense).
    return firstResponse;
  }

  // 401 on a trigger host. Try silent refresh.
  // ISS-377 NB-2: the refresh call is wrapped in a module-scoped
  // single-flight coalescer so two concurrent apiFetch 401s
  // produce ONE signinSilent() call instead of two. See
  // `singleFlight.ts` for the slot lifecycle.
  //
  // ISS-377 round 2.1 (R2-B1): capture the freshly-resolved User
  // from signinSilent. The accessor ref (`authRef.current` in
  // `OidcUserAccessorBridge`) is the React RENDER-time snapshot.
  // Between the moment signinSilent() resolves and the moment
  // React re-renders the bridge, the ref still holds the PRE-
  // refresh User (with the OLD expired access_token). Re-reading
  // the accessor for the replay would re-attach the stale token
  // — the round-1 + round-2 fix that the architect flagged in
  // note 13305 (R2-B1). Threading the resolved User directly
  // into the replay's auth strategy avoids the read.
  let refreshDidSucceed = false;
  let refreshedUser: unknown = null;
  try {
    refreshedUser = await signinSilentRefresh(() =>
      (
        authStrategy.auth as unknown as {
          signinSilent: () => Promise<unknown>;
        }
      ).signinSilent(),
    );
    refreshDidSucceed = true;
  } catch {
    // Refresh failed. Per BA note_13124, the user is redirected
    // to the login flow; the original 401 is returned to the
    // caller (NOT a silent auto-replay — that was the round-1
    // mistake). The replay-guard architecture was REMOVED in round 2;
    // the per-call `init` cache below is sufficient to make the
    // replay deterministic if the caller's own re-render-driven
    // retry path wants to re-issue the same call with the new token.
    redirectToLogin({
      auth: authStrategy.auth,
      returnTo: currentReturnTo(),
    });
    return firstResponse;
  }

  if (!refreshDidSucceed) {
    // Defensive: the only way we exit the try-without-throw branch
    // is on success. This branch is unreachable in practice but
    // keeps TypeScript happy if the single-flight wrapper changes.
    return firstResponse;
  }

  // Refresh succeeded — replay the original request with the
  // freshly-minted bearer. ISS-377 round 2.1 (R2-B1): the
  // replay's auth strategy is built from the freshly-resolved
  // User (captured above), NOT by re-reading the accessor. The
  // accessor is `() => authRef.current` (the React render-time
  // snapshot); after signinSilent() resolves, the React re-render
  // is scheduled but not yet executed — the ref still holds the
  // PRE-refresh User. Re-reading it would re-attach the OLD
  // expired token (the round-2.1 bug the architect flagged in
  // note 13305, R2-B1). Threading the resolved User directly
  // into the replay's auth strategy is the architect-prescribed
  // fix: "take the token directly from `signinSilent()`'s resolved
  // User (or `userManager.getUser()`); do NOT read React state."
  //
  // Fallback order:
  //   1. Caller passed `auth` explicitly → use it verbatim
  //      (the operator-token path or a custom AuthContextProps).
  //   2. signinSilent resolved with a non-null User → build a
  //      one-off OIDC strategy with the fresh User spread over
  //      the prior `authStrategy.auth` (preserves the
  //      signinSilent / signinRedirect / events references the
  //      rest of the auth state needs).
  //   3. signinSilent resolved with null (signed out during
  //      refresh) → fall through to the accessor read; that
  //      will also yield null and apiFetch will return the
  //      original 401 to the caller without a bearer.
  const firstHeaders = normaliseHeaders(init.headers);
  const replayIdemKey = firstHeaders["X-Idempotency-Key"];
  const replayAuthStrategy: ApiAuthStrategy =
    options.auth !== undefined
      ? authStrategy
      : refreshedUser !== null && refreshedUser !== undefined
        ? {
            kind: "oidc",
            auth: {
              ...(authStrategy.auth as unknown as Record<string, unknown>),
              user: refreshedUser,
            } as unknown as AuthContextProps,
          }
        : resolveAuthStrategyForRequest({});
  const replayHeaders = attachAuthHeaders({
    authStrategy: replayAuthStrategy,
    requestUrl: url,
    allowCrossOrigin: options.allowCrossOrigin === true,
    existingHeaders: stripClientInjectedHeaders(firstHeaders),
  });
  if (typeof replayIdemKey === "string" && replayIdemKey.length > 0) {
    replayHeaders["X-Idempotency-Key"] = replayIdemKey;
  }
  const replayInit: RequestInit = {
    ...init,
    headers: replayHeaders,
  };
  return window.fetch(url, replayInit);
}

// `getApiBase` re-export — convenience for callers that want to
// compose URLs (`apiFetch(`${getApiBase()}/people/${id}`)`) without
// importing from `@/runtime/apiRuntime` directly. The implementation
// lives in `apiRuntime.ts` so the runtime config is the single
// source of truth. Re-exported from `./api/index.ts` instead of
// directly to keep this file's imports linear.
export { getApiBase } from "@/runtime/apiRuntime";

// Re-export the operator-token snapshot type so callers can type
// their `useOperatorToken()` return without an extra import line.
export type { OperatorTokenSnapshot };
